import { Button, Container, Form, ProgressBar } from "react-bootstrap";
import React, { useState, useEffect } from "react";
import { useApi } from "../../contexts/ApiProvider";
import { useDispatch } from "react-redux";
import { userActions } from "../../actions/UserActions";

export function ConsentBox() {
  const dispatch = useDispatch();
  const api = useApi();

  const [tickConsentAge, setTickConsentAge] = useState(false);
  const [tickConsent, setTickConsent] = useState(false);
  const [signature, setSignature] = useState("");
  const [currentDate, setCurrentDate] = useState("");

  useEffect(() => {
    const today = new Date();
    setCurrentDate(today.toISOString().split("T")[0]);
  }, []);

  const handleConsentAgeChange = (event) => {
    setTickConsentAge(event.target.checked);
  };

  const handleConsentChange = (event) => {
    setTickConsent(event.target.checked);
  };

  const handleSignatureChange = (event) => {
    setSignature(event.target.value);
  };

  const handleSubmit = async () => {
    const consentForm = {
      consent_age: tickConsentAge,
      privacy_consent: tickConsent,
      signature: signature,
      date: currentDate,
    };
    
    dispatch(userActions.consentSigned());
    await api.post(`/me/consent`, {
      ...consentForm,
      submitted: true
    });
  };


  return (
    <Container style={{ position: "relative", top: "30px", height: "80vh", overflowY: "auto" }}>
      <div className="progress-container">
        <div style={{ width: "100%", textAlign: "center", marginBottom: "10px" }}>
          <ProgressBar>
            <ProgressBar now={20} label="1. Introduction" key={1} variant="secondary" />
            <ProgressBar now={20} label="2. Participant Information & Consent" key={2} variant="primary" />
            <ProgressBar now={20} label="3. Pre-study Survey" key={3} variant="secondary" />
            <ProgressBar now={20} label="4. Main Study" key={4} variant="secondary" />
            <ProgressBar now={20} label="5. Post-study Survey" key={5} variant="secondary" />
          </ProgressBar>
        </div>
      </div>
      <div className="consent-popup-container">
        <div style={{ maxHeight: "76.5vh", overflowY: "auto", padding: "10px" }}>
        <p style={{ textAlign: "center", fontSize: "11px", color: "#093a73" }}>⇣ Please scroll and complete this form. ⇣</p>

          <h2 style={{textAlign: "center", color: "#093a73", fontSize: 36}}>Participant Information Sheet</h2>
          <h5>Evaluation of an Infection Prevention and Control (IPC) Dashboard to Track and Interrupt Spread of Healthcare-associated Infections</h5>
          <p>CUREC Approval Reference: R94653/RE002</p>
          <br />
          <h6><b><i>General Infromation</i></b></h6>
          <p>The aim of this research is to investigate if providing infection prevention and control (IPC) practitioners with curated information on patient admissions, ward movements, infection tests and pathogen genomic data through a web-based dashboard can help them better track, manage and control the further spread of healthcare-associated infections in hospitals.</p>
          <p>We appreciate your interest in participating in this online task. You have been invited to participate as you are a practising (or recently retired) IPC practitioner (e.g., specialist nurse, doctor, or other healthcare professional with direct responsibilities or roles in IPC) with more than one year’s professional experience.</p>
          <p>You may ask any questions before deciding to take part (if you wish to) by contacting the researchers (details below).</p>
          <p>The lead researcher is Dr Augustine Luk, who is based at the Nuffield Department of Medicine, University of Oxford. This research is being completed under the supervision of Professor David Eyre and Professor Ann Sarah Walker.</p>
          <p>If you participate, you will be presented with a desktop-based simulation of up to 20 real-world cases of <i>Clostridioides difficile</i> (also known as C. diff) infections. You will be asked to assess each case first using only traditional contact tracing methods, then with added patient movement and ward overlap information shown in interactive diagrams, and finally with added pathogen genomic information from whole-genome sequencing. With each new piece of information, you will be asked to give your opinion on the most likely source(s) and location(s) of transmission and suggest appropriate course(s) of action (e.g., ward decontamination). Additionally, you will be asked to complete pre-study and post-study online surveys to collect baseline participant characteristics and obtain feedback on the simulation platform and the usefulness of the added patient movement and genomic data. This should take about 2 hours in total and can be completed in multiple sessions. You are requested to complete the study within 2 months. Training on how to use the web platform will be provided in the form of a video.</p>
          <p>The data collected will be used by the named researchers to understand how IPC practitioners utilise detailed patient and pathogen information. The study aims to identify ways to improve contact tracing and infection control measures and determine whether additional information can make the process of tracking infections more efficient and accurate, uncovering transmission links that traditional methods might miss.</p>
          <br />
          <h6><b><i>Do I have to take part?</i></b></h6>
          <p>No. Please note that participation is voluntary. If you do decide to take part, you may withdraw at any point for any reason by exiting the online application and closing the browser. Unless otherwise requested in writing, data collected up to the point of withdrawal will be kept and used for future analysis.</p>
          <br />
          <h6><b><i>How will my data be used?</i></b></h6>
          <p>The data we will collect that could identify you will be your name (recorded as part of the consent process), age, contact details, and professional details (e.g., your current role and position, and years of experience). Your IP address will not be stored. We will take all reasonable measures to ensure that data remain confidential.</p>
          <p>The responses you provide will be stored in a password-protected electronic database on a secure server and may be used in academic publications, conference presentations and academic theses.  Research data will be stored for three years after publication or public release of the work of the research. </p>
          <br />
          <h6><b><i>Who will have access to my data?</i></b></h6>
          <p>The University of Oxford is the data controller with respect to your personal data and, as such, will determine how your personal data is used in the research. The University will process your personal data for the purpose of the research outlined above. Research is a task that we perform in the public interest. Further information about your rights with respect to your personal data is available from <a href="https://compliance.admin.ox.ac.uk/individual-rights">https://compliance.admin.ox.ac.uk/individual-rights</a>.</p>
          <p>We would like to use the data in future studies, and to share data with other researchers (e.g. in online databases). Data will have identifying information removed before it is shared with other researchers or results are made public.</p>
          <p>The results will be written up for a DPhil degree.</p>
          <br />
          <h6><b><i>Who has reviewed this research?</i></b></h6>
          <p>This research has been reviewed by, and received ethics clearance through, a subcommittee of the University of Oxford Central University Research Ethics Committee [R94653/RE001].</p>
          <br />
          <h6><b><i>Who do I contact if I have a concern or I wish to complain?</i></b></h6>
          <p>If you have a concern about any aspect of this research, please contact Dr Augustine Luk (<a href="mailto:augustine.luk@ndm.ox.ac.uk">augustine.luk@ndm.ox.ac.uk</a>) or their supervisor Professor David Eyre (<a href="mailto:david.eyre@bdi.ox.ac.uk">david.eyre@bdi.ox.ac.uk</a>), and we will do our best to answer your query. We will acknowledge your concern within 10 working days and give you an indication of how it will be dealt with. If you remain unhappy or wish to make a formal complaint, please contact the University of Oxford Research Governance, Ethics & Assurance (RGEA) team at <a href="mailto:rgea.complaints@admin.ox.ac.uk">rgea.complaints@admin.ox.ac.uk</a> or on 01865 616480.</p>
          <br />
          <h6><b>Please note that you may only participate in this survey if you are 18 years of age or over.</b></h6>
          <Form>
          <Form.Group controlId="formBasicCheckbox">
            <Form.Check
              type="checkbox"
              id="consentAge"
              label="I certify that I am 18 years of age or over."
              checked={tickConsentAge}
              onChange={handleConsentAgeChange}
            />
            <br />
            <h6><b>I have read the information contained in the Participant Information Sheet and agree to participate with the understanding that the data (including any personal data) I submit will be processed accordingly, please tick the box below to consent to take part.</b></h6>
            <Form.Check
              type="checkbox"
              id="consent"
              label="Yes, I agree to take part."
              checked={tickConsent}
              onChange={handleConsentChange}
            />
          </Form.Group>

          <Form.Group controlId="signature" style={{ marginTop: "20px" }}>
            <Form.Label><b>Please type your name to confirm your agreement:</b></Form.Label>
            <Form.Control
              type="text"
              value={signature}
              onChange={handleSignatureChange}
              placeholder="Your Name"
            />
          </Form.Group>

          <Form.Group controlId="date" style={{ marginTop: "10px" }}>
            <Form.Label><b>Date:</b></Form.Label>
            <Form.Control type="text" value={currentDate} readOnly />
          </Form.Group>

          <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
            <Button
              className="blue-button-consent"
              disabled={!tickConsent || !tickConsentAge || !signature}
              onClick={handleSubmit}
            >
              {tickConsent && tickConsentAge && signature ? "Submit & Proceed" : "Please complete the form to proceed"}
            </Button>
          </div>
          <p style={{ marginTop: "10px", textAlign: "center", color: "grey"}}>
          {!tickConsent || !tickConsentAge || !signature ? "If you do not wish to participate, you may close this window instead." : ""}
          </p>
        </Form>
        </div>
      </div>
    </Container>
  );
}